import { CumulativeRainfall } from './CumulativeRainFall'
import { Legend } from './Legend'

export type RepresentationTypes = 'rainfall' | 'return period' | 'flood risk' | 'flood depth' | 'cumulative rainfall'

export type RegularRepresentationTypes = Exclude<RepresentationTypes, 'cumulative rainfall'>

type BaseRepresentation<T extends RepresentationTypes> = {
  type: T
  translationKey: string
  unitTranslationKey: string
  legend: Legend
  dynamicPropertyName: string
  requiresCatchments: boolean
  hasSingularForm: boolean
  hidden: boolean
  format(value: number): string
}

export type CumulativeRainfallRepresentation = {
  cumulativeRainfall: CumulativeRainfall
} & BaseRepresentation<'cumulative rainfall'>

export type RegularRepresentation = BaseRepresentation<RegularRepresentationTypes>

export type Representation<T extends RepresentationTypes> = T extends 'cumulative rainfall'
  ? CumulativeRainfallRepresentation
  : BaseRepresentation<T>

let rainfallandcumulativeLegend = new Legend('model.representation.rainfall', [])

export function setCumulativeLegend(legend: Legend): void {
  rainfallandcumulativeLegend = legend
}

export const rainfallRepresentation: Representation<'rainfall'> = {
  type: 'rainfall',
  translationKey: 'model.representation.rainfall',
  unitTranslationKey: 'model.representation.rainfall.unit',
  legend: rainfallandcumulativeLegend,
  dynamicPropertyName: 'intensity',
  requiresCatchments: false,
  hasSingularForm: false,
  hidden: false,
  format: (value) => `${value.toFixed(0)}`,
}

export function createCumulativeRainfallRepresentation(
  cumulativeRainfall: CumulativeRainfall,
): CumulativeRainfallRepresentation {
  return {
    type: 'cumulative rainfall',
    translationKey: 'model.representation.cumulative_rainfall',
    unitTranslationKey: 'model.representation.cumulative_rainfall.unit',
    legend: rainfallandcumulativeLegend,
    dynamicPropertyName: 'intensity',
    requiresCatchments: false,
    hasSingularForm: false,
    cumulativeRainfall,
    hidden: false,
    format: (value) => `${value.toFixed(0)}`,
  }
}

const returnPeriodLegend = new Legend('model.representation.return_period', [])
export const returnPeriodRepresentation: Representation<'return period'> = {
  type: 'return period',
  translationKey: 'model.representation.return_period',
  unitTranslationKey: 'model.representation.return_period.unit',
  legend: returnPeriodLegend,
  dynamicPropertyName: 'rt',
  requiresCatchments: false,
  hasSingularForm: true,
  hidden: false,
  format: (value) => {
    if (value < 1 && value.toFixed(0) != '1') {
      return `${value.toFixed(1)}`
    } else if (value > 1000) {
      return '> 1000'
    } else {
      return `${value.toFixed(0)}`
    }
  },
}

const floodRiskLegend = new Legend('model.representation.flood_risk', [], 'distributed')
export const floodRiskRepresentation: Representation<'flood risk'> = {
  type: 'flood risk',
  translationKey: 'model.representation.flood_risk',
  unitTranslationKey: 'model.representation.flood_risk.unit',
  legend: floodRiskLegend,
  dynamicPropertyName: 'risk',
  requiresCatchments: false,
  hasSingularForm: false,
  hidden: true,
  format(value: number): string {
    if (value === undefined || value === null) {
      return ''
    }
    const thresholds = this.legend.thresholds
    for (let i = thresholds.length - 1; i >= 0; --i) {
      const threshold = thresholds[i]
      if (value >= threshold.value) {
        return threshold.label
      }
    }
    return thresholds[0].label
  },
}

const floodDepthLegend = new Legend('model.representation.flood_depth', [])
export const floodDepthRepresentation: Representation<'flood depth'> = {
  type: 'flood depth',
  translationKey: 'model.representation.flood_depth',
  unitTranslationKey: 'model.representation.flood_depth.unit',
  legend: floodDepthLegend,
  dynamicPropertyName: 'FloodRisk',
  requiresCatchments: true,
  hasSingularForm: false,
  hidden: false,
  format: (value) => `${value.toFixed(2)}`,
}

type RegularRepresentationMap = {
  [K in RegularRepresentationTypes]: BaseRepresentation<K>
}

export const regularRepresentations: RegularRepresentationMap = {
  rainfall: rainfallRepresentation,
  'return period': returnPeriodRepresentation,
  'flood risk': floodRiskRepresentation,
  'flood depth': floodDepthRepresentation,
} as const

export function getAllRegularRepresentations(): Representation<RegularRepresentationTypes>[] {
  return Object.values(regularRepresentations)
}
